<template>
    <div
        ref="container"
        class="image-lazy-container w-full h-full relative overflow-hidden"
        :class="containerClass"
    >
        <!-- Loading占位符 -->
        <div
            v-if="!isLoaded || isLoading"
            class="absolute inset-0 flex items-center justify-center"
            :class="placeholderClass"
        >
            <!-- 自定义loading插槽 -->
            <slot
                v-if="$slots.loading && isIntersecting && isLoading"
                name="loading"
            ></slot>
            <!-- 默认loading -->
            <div
                v-else-if="isIntersecting && isLoading"
                class="animate-spin rounded-full h-10 w-10 border-b-2 primary-border"
            ></div>
            <!-- 未进入视口时的占位符 -->
            <div
                v-else
                class="w-full h-full bg-gradient-to-br from-gray-700 to-gray-800 animate-pulse"
                :class="placeholderClass"
            ></div>
        </div>

        <!-- 实际内容 -->
        <!--
          注意：对 video 来说，反复 v-if 挂载/卸载会导致浏览器重建解码器并触发新的 Range(206) 请求。
          这里对 video 做“首次进入视口后常驻”，离屏时仅 pause，避免重复加载与首播等待。
        -->
        <template v-if="isIntersecting || (isVideo && hasEverIntersected)">
            <!-- 图片 -->
            <img
                v-if="!isVideo"
                :src="src"
                :alt="alt"
                class="w-full h-full object-cover object-top transition-opacity duration-300"
                :class="[imageClass, { 'opacity-0': isLoading }]"
                @load="handleLoad"
                @error="handleError"
            />

            <!-- 视频 -->
            <video
                v-else
                ref="videoEl"
                :src="src"
                class="w-full h-full object-cover object-top transition-opacity duration-300"
                :class="[imageClass, { 'opacity-0': isLoading }]"
                muted
                loop
                :autoplay="autoplay"
                playsinline
                webkit-playsinline
                preload="metadata"
                @loadeddata="handleLoad"
                @error="handleError"
            />
        </template>

        <!-- 错误状态 -->
        <div
            v-if="hasError"
            class="absolute inset-0 flex items-center justify-center bg-gray-800"
            :class="placeholderClass"
        >
            <slot v-if="$slots.error" name="error">
                <div class="text-center text-gray-400">
                    <div class="text-2xl mb-2">📷</div>
                    <div class="text-xs">{{ t('common.loadFailed') }}</div>
                </div>
            </slot>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { analyticsEvent } from '~/utils/log-event-func'
import { hasReportedExposure, recordExposure } from '~/utils/exposure-cache'
import { useI18n } from '~/composables/useI18n'

// 国际化
const { t } = useI18n()

interface Props {
    src: string
    alt?: string
    isVideo?: boolean
    autoplay?: boolean
    threshold?: number
    containerClass?: string
    imageClass?: string
    placeholderClass?: string
    rootMargin?: string
    allowLoad?: boolean // 是否允许开始加载图片
    // 埋点相关参数
    characterId?: string // 角色ID，用于曝光埋点
    exposurePage?: 'home_page' | 'character_list_page' // 曝光页面类型
}

const props = withDefaults(defineProps<Props>(), {
    alt: '',
    isVideo: false,
    autoplay: false,
    threshold: 0.1,
    containerClass: '',
    imageClass: '',
    placeholderClass: '',
    rootMargin: '50px',
    allowLoad: true,
})

const emit = defineEmits<{
    load: [src: string]
    error: [src: string]
    intersect: [isIntersecting: boolean]
}>()

// 响应式状态
const container = ref<HTMLElement>()
const isIntersecting = ref(false)
const hasEverIntersected = ref(false)
const isLoading = ref(false)
const isLoaded = ref(false)
const hasError = ref(false)
const videoEl = ref<HTMLVideoElement | null>(null)

let observer: IntersectionObserver | null = null

// 处理图片/视频加载完成
const handleLoad = () => {
    isLoading.value = false
    isLoaded.value = true
    hasError.value = false
    emit('load', props.src)
}

// 处理加载错误
const handleError = () => {
    isLoading.value = false
    isLoaded.value = false
    hasError.value = true
    emit('error', props.src)
}

// 重置状态
const resetState = () => {
    isLoading.value = false
    isLoaded.value = false
    hasError.value = false
}

// 监听src变化，重置状态
watch(
    () => props.src,
    () => {
        resetState()
        if (isIntersecting.value && props.allowLoad) {
            isLoading.value = true
        }
    }
)

// 监听allowLoad变化，控制是否允许加载
watch(
    () => props.allowLoad,
    (newAllowLoad, oldAllowLoad) => {
        if (newAllowLoad && !oldAllowLoad) {
            // 从不允许加载变为允许加载，重新设置Observer
            cleanupObserver()
            setupObserver()
        } else if (!newAllowLoad && oldAllowLoad) {
            // 从允许加载变为不允许加载，清理Observer并重置状态
            cleanupObserver()
            isIntersecting.value = false
            isLoading.value = false
        }
    }
)

// 设置Intersection Observer
const setupObserver = () => {
    if (
        !props.allowLoad ||
        !container.value ||
        typeof IntersectionObserver === 'undefined'
    ) {
        // 如果不允许加载或不支持Intersection Observer，保持占位符状态
        if (!props.allowLoad) {
            isIntersecting.value = false
            isLoading.value = false
            return
        }
        // 如果不支持Intersection Observer但允许加载，直接加载
        isIntersecting.value = true
        isLoading.value = true
        return
    }

    observer = new IntersectionObserver(
        entries => {
            entries.forEach(entry => {
                const wasIntersecting = isIntersecting.value
                isIntersecting.value = entry.isIntersecting
                if (entry.isIntersecting) hasEverIntersected.value = true

                emit('intersect', entry.isIntersecting)

                // 第一次进入视口时开始加载（仅当允许加载时）
                if (
                    props.allowLoad &&
                    entry.isIntersecting &&
                    !wasIntersecting &&
                    !isLoaded.value &&
                    !hasError.value
                ) {
                    isLoading.value = true
                }

                // 埋点：角色卡片曝光（进入视口70%时触发）
                if (
                    entry.isIntersecting &&
                    entry.intersectionRatio >= 0.7 && // 元素至少70%可见时才上报
                    props.characterId &&
                    props.exposurePage
                ) {
                    // 检查是否已在当前页面上报
                    if (
                        !hasReportedExposure(
                            props.characterId,
                            props.exposurePage
                        )
                    ) {
                        try {
                            analyticsEvent('view_character_card', {
                                character_id: props.characterId,
                                exposure_page: props.exposurePage,
                            })
                            // 记录已上报（按页面区分）
                            recordExposure(
                                props.characterId,
                                props.exposurePage
                            )
                        } catch (error) {
                            console.error('角色曝光埋点上报失败:', error)
                        }
                    }
                }
            })
        },
        {
            threshold: [props.threshold, 0.7], // 同时监听 0.1 和 0.7 两个阈值
            rootMargin: props.rootMargin,
        }
    )

    observer.observe(container.value)
}

// 清理Observer
const cleanupObserver = () => {
    if (observer && container.value) {
        observer.unobserve(container.value)
        observer.disconnect()
        observer = null
    }
}

// video：离屏 pause；回到视口且允许 autoplay 时尝试恢复播放（避免反复重建导致重复 206）
watch(
    () => [isIntersecting.value, props.autoplay, props.isVideo] as const,
    ([inView, autoplay, isVideo]) => {
        if (!isVideo) return
        const v = videoEl.value
        if (!v) return

        if (!inView) {
            if (!v.paused) v.pause()
            return
        }

        if (autoplay && v.paused) {
            void v.play().catch(() => {
                // ignore (可能被浏览器策略阻止或快速滚动切换)
            })
        }
    }
)

onMounted(() => {
    setupObserver()
})

onUnmounted(() => {
    cleanupObserver()
})

// 暴露给父组件的方法
defineExpose({
    reload: () => {
        resetState()
        if (isIntersecting.value) {
            isLoading.value = true
        }
    },
    forceLoad: () => {
        isIntersecting.value = true
        isLoading.value = true
    },
})
</script>

<style scoped lang="less">
/* 懒加载图片容器样式 */

/* 主题色相关样式 - 与 index.vue 保持一致 */
.primary-border {
    border-color: @tag-selected-bg;
}
</style>
